import { Action, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import _ from 'lodash';
import { createSelector } from 'reselect';
import moment from 'moment';
import { IAppState } from '../reducers';
import { padGameSeason, useCalendarSplit } from '../../utils/exchange';
import {
  defaultExchangeFilters,
  defaultExchangeSearchParams,
} from '../../api/vault/searchEngine/api';
import {
  getTeams,
  getConferences,
} from '../../api/vault/teams';
import { getSchedules } from '../../api/vault/exchangeSchedule';
import { ActionType as ExchangeSearchResultsAction, initialState } from './searchResults';
import { getLookups } from '../lookups';
import { selectIsUserExchangeVendor, selectUserOrgIds } from '../user/userOrgRole';
import { getVendors } from '../../api/vault/vendors';
import { resetActiveAsset } from './activeAsset';

const TEAMS_SELECTION_STORAGE_KEY = 'catapult:exchangeSelectedTeams';
const SEASON_FILTER_STORAGE_KEY = 'catapult:exchangeSeasonFilter';

export enum DownloadFilter {
  Downloaded = 'Downloaded',
  NotDownloaded = 'NotDownloaded',
}

export enum PushToCloudFilter {
  PushedToCloud = 'PushedToCloud',
  NotPushedToCloud = 'NotPushedToCloud',
}

export type DateSelectionType = 'dateRange';
interface IDateSelectionTypes {
  DATE_RANGE: DateSelectionType;
}
export const DATE_SELECTION_TYPE: IDateSelectionTypes = {
  DATE_RANGE: 'dateRange',
};

export interface ITeamData {
  teamId: string;
  teamCode: string;
  teamName: string;
  conferenceName: string;
  division: string;
  teamImageFile: string;
}

interface ITeam extends ITeamData {
  isSelected: boolean;
  isFocused: boolean;
}

export interface IConference {
  _id: string;
  conferenceName: string;
  teams: ITeam[];
  isExpanded: boolean;
  isAllTeamsSelected: boolean;
  conferenceImageFile: string;
}

export type SeasonMap = { [key: string]: string };

export interface IState {
  dateSelectionType: DateSelectionType;
  dateRange: {
    from: Date,
    to: Date,
  },
  seasons: Array<string>;
  seasonMap: SeasonMap;
  vendorSports: string[];
  sportFilter: string;
  conferences: IConference[];
  teamImageMap: { [key: string]: string };
  isLoaded: boolean;
  isFilterByQualifier: boolean;
  qualifierFilter: Array<string>;
  isFilterByDownload: boolean;
  downloadFilter: DownloadFilter;
  isFilterBySeason: boolean;
  seasonFilter: string;
  isFilterByFileType: boolean;
  fileTypeFilter: Array<string>;
  teamFilter: string;
}

export enum ActionType {
  ChangeDateSelectionType = 'ExchangeSearchParams.ChangeDateSelectionType',
  ChangeDateFrom = 'ExchangeSearchParams.ChangeDateFrom',
  ChangeDateTo = 'ExchangeSearchParams.ChangeDateTo',
  ClearDateSelection = 'ExchangeSearchParams.ClearDateSelection',
  ToggleConferenceExpandedState = 'ExchangeSearchParams.ToggleConferenceExpandedState',
  ToggleConferenceAllExpandedState = 'ExchangeSearchParams.ToggleConferenceAllExpandedState',
  ToggleAllTeamsSelection = 'ExchangeSearchParams.ToggleAllTeamsSelection',
  ToggleTeamSelection = 'ExchangeSearchParams.ToggleTeamSelection',
  ClearTeamSelection = 'ExchangeSearchParams.ClearTeamSelection',
  ToggleTeamFocus = 'Exchange.SearchParams.ToggleTeamFocusc',
  ToggleFilterByQualifier = 'ExchangeSearchParams.ToggleFilterByQualifier',
  ToggleFilterByDownload = 'ExchangeSearchParams.ToggleFilterByDownload',
  ToggleFilterBySeason = 'ExchangeSearchParams.ToggleFilterBySeason',
  ToggleFilterByFileType = 'ExchangeSearchParams.ToggleFilterByFileType',
  LoadTeams = 'ExchangeSearchParams.LoadTeams',
  ClearTeams = 'ExchangeSearchParams.ClearTeams',
  LoadSeasons = 'ExchangeSearchParams.LoadSeasons',
  UpdateSportFilter = 'ExchangeSearchParams.UpdateSportFilter',
  UpdateVendorSports = 'ExchangeSearchParams.UpdateVendorSports',
  UpdateTeamFilter = 'ExchangeSearchParams.UpdateTeamFilter',
}

export interface IExchangeSearchParamsAction extends Action<ActionType> {
  payload?: any,
}

type IExchangeSearchParamsThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  IAppState,
  unknown,
  IExchangeSearchParamsAction
>

const getCachedSeasonOptions = (): Array<string> => {
  const cached = localStorage.getItem(SEASON_FILTER_STORAGE_KEY);
  if (cached) {
    return [cached];
  }
  return [];
};

export const INITIAL_STATE: IState = {
  dateSelectionType: DATE_SELECTION_TYPE.DATE_RANGE,
  dateRange: {
    from: null,
    to: null,
  },
  seasons: getCachedSeasonOptions(),
  seasonMap: null,
  vendorSports: [],
  sportFilter: null,
  conferences: [],
  teamImageMap: {},
  isLoaded: false,
  isFilterByQualifier: false,
  qualifierFilter: [],
  isFilterByDownload: false,
  downloadFilter: null,
  isFilterBySeason: !!localStorage.getItem(SEASON_FILTER_STORAGE_KEY),
  seasonFilter: localStorage.getItem(SEASON_FILTER_STORAGE_KEY),
  isFilterByFileType: false,
  fileTypeFilter: null,
  teamFilter: '',
};

function handleConferenceExpandedStateToggle(state: IState, action: IExchangeSearchParamsAction): IState {
  return {
    ...state,
    conferences: (state.conferences || []).reduce((conferences, conference, conferenceIndex) => {
      if (conferenceIndex === action.payload.conferenceIndex) {
        conferences.push({
          ...conference,
          isExpanded: !conference.isExpanded,
        });
      } else {
        conferences.push(conference);
      }
      return conferences;
    }, []),
  };
}

function handleConferenceExpandedStateToggleAll(state: IState, action: IExchangeSearchParamsAction): IState {
  const conferences = [...(state.conferences || [])];
  return {
    ...state,
    conferences: conferences.reduce((conferences, conference) => {
      conferences.push({
        ...conference,
        isExpanded: action.payload.isExpanded,
      });
      return conferences;
    }, []),
  };
}

function handleAllTeamsSelectionToggle(state: IState, action: IExchangeSearchParamsAction): IState {
  return {
    ...state,
    conferences: (state.conferences || []).reduce((conferences, conference, conferenceIndex) => {
      if (conferenceIndex === action.payload.conferenceIndex) {
        const teams = conference.teams.map((team) => ({
          ...team,
          isSelected: !conference.isAllTeamsSelected,
        }));
        conferences.push({
          ...conference,
          teams,
          isAllTeamsSelected: !conference.isAllTeamsSelected,
        });
      } else {
        conferences.push(conference);
      }
      return conferences;
    }, []),
  };
}

function handleTeamSelectionToggle(state: IState, action: IExchangeSearchParamsAction): IState {
  const conferences = [...state.conferences];
  return {
    ...state,
    conferences: conferences.reduce((conferences, conference) => {
      if (conference.conferenceName === action.payload.conferenceName) {
        const teams = conference.teams.map((team) => {
          if (team.teamId === action.payload.teamId) {
            return {
              ...team,
              isSelected: !team.isSelected,
              isFocused: false,
            };
          }
          return team;
        });
        conferences.push({
          ...conference,
          teams,
          isAllTeamsSelected: !teams.some(({ isSelected }) => !isSelected),
        });
      } else {
        conferences.push(conference);
      }
      return conferences;
    }, []),
  };
}

function handleTeamSelectionClear(state: IState, action: IExchangeSearchParamsAction): IState {
  return {
    ...state,
    conferences: state.conferences.map((conference) => {
      const teams = conference.teams.map((team) => ({
        ...team,
        isSelected: false,
        isFocused: false,
      }));

      return {
        ...conference,
        teams,
        isAllTeamsSelected: false,
      };
    }, []),
  };
}

function handleTeamFocusToggle(state: IState, action: IExchangeSearchParamsAction): IState {
  return {
    ...state,
    conferences: (state.conferences || []).reduce((conferences, conference, conferenceIndex) => {
      if (conferenceIndex === action.payload.conferenceIndex) {
        const teams = conference.teams.map((team, teamIndex) => {
          if (teamIndex === action.payload.teamIndex) {
            return {
              ...team,
              isFocused: !team.isFocused,
            };
          }
          return team;
        });
        conferences.push({
          ...conference,
          teams,
        });
      } else {
        conferences.push(conference);
      }
      return conferences;
    }, []),
  };
}

const changeSportClear = {
  conferences: INITIAL_STATE.conferences,
  teamImageMap: INITIAL_STATE.teamImageMap,
  dateRange: INITIAL_STATE.dateRange,
  isLoaded: INITIAL_STATE.isLoaded,
  isFilterByQualifier: INITIAL_STATE.isFilterByQualifier,
  qualifierFilter: INITIAL_STATE.qualifierFilter,
  isFilterByDownload: INITIAL_STATE.isFilterByDownload,
  downloadFilter: INITIAL_STATE.downloadFilter,
  isFilterBySeason: INITIAL_STATE.isFilterBySeason,
  seasonFilter: INITIAL_STATE.seasonFilter,
  isFilterByFileType: INITIAL_STATE.isFilterByFileType,
  fileTypeFilter: INITIAL_STATE.fileTypeFilter,
};

export default function reducer(state: IState = INITIAL_STATE, action: IExchangeSearchParamsAction): IState {
  switch (action.type) {
    case ActionType.ChangeDateSelectionType:
      return {
        ...state,
        dateSelectionType: action.payload,
      };
    case ActionType.ChangeDateFrom:
      return {
        ...state,
        dateRange: {
          ...state.dateRange,
          from: action.payload,
        },
      };
    case ActionType.ChangeDateTo:
      return {
        ...state,
        dateRange: {
          ...state.dateRange,
          to: action.payload,
        },
      };
    case ActionType.UpdateTeamFilter:
      return {
        ...state,
        teamFilter: action.payload,
      };
    case ActionType.ToggleConferenceExpandedState:
      return handleConferenceExpandedStateToggle(state, action);
    case ActionType.ToggleConferenceAllExpandedState:
      return handleConferenceExpandedStateToggleAll(state, action);
    case ActionType.ToggleAllTeamsSelection:
      return handleAllTeamsSelectionToggle(state, action);
    case ActionType.ToggleTeamSelection:
      return handleTeamSelectionToggle(state, action);
    case ActionType.ClearTeamSelection:
      return handleTeamSelectionClear(state, action);
    case ActionType.ToggleTeamFocus:
      return handleTeamFocusToggle(state, action);
    case ActionType.ToggleFilterByQualifier:
      return {
        ...state,
        isFilterByQualifier: !!action.payload.qualifier,
        qualifierFilter: action.payload.qualifier,
      };
    case ActionType.ToggleFilterByDownload:
      return {
        ...state,
        isFilterByDownload: !!action.payload.downloadFilter,
        downloadFilter: action.payload.downloadFilter,
      };
    case ActionType.ToggleFilterBySeason:
      localStorage.setItem(SEASON_FILTER_STORAGE_KEY, action.payload.seasonFilter || '');
      return {
        ...state,
        isFilterBySeason: !!action.payload.seasonFilter,
        seasonFilter: action.payload.seasonFilter,
      };
    case ActionType.ToggleFilterByFileType:
      return {
        ...state,
        isFilterByFileType: !!action.payload.fileTypeFilter,
        fileTypeFilter: action.payload.fileTypeFilter,
      };
    case ActionType.LoadTeams:
      return {
        ...state,
        conferences: action.payload.conferences,
        teamImageMap: action.payload.teamImageMap,
        isLoaded: true,
      };
    case ActionType.ClearTeams:
      return {
        ...state,
        conferences: [],
        teamImageMap: {},
        isLoaded: false,
      };
    case ActionType.LoadSeasons:
      return {
        ...state,
        seasons: action.payload.seasons,
        seasonMap: action.payload.seasonMap,
      };
    case ActionType.ClearDateSelection:
      return {
        ...state,
        dateRange: INITIAL_STATE.dateRange,
      };
    case ActionType.UpdateSportFilter:
      return {
        ...state,
        ...changeSportClear,
        vendorSports: state.vendorSports,
        sportFilter: action.payload.sportFilter,
      };
    case ActionType.UpdateVendorSports:
      return {
        ...state,
        vendorSports: action.payload.vendorSports,
      };
    default:
      return state;
  }
}

export const changeDateSelectionType = (type: DateSelectionType) => ({
  type: ActionType.ChangeDateSelectionType,
  payload: type,
});

export const changeDateFrom = (date: Date) => ({
  type: ActionType.ChangeDateFrom,
  payload: date,
});

export const changeDateTo = (date: Date) => ({
  type: ActionType.ChangeDateTo,
  payload: date,
});

export const updateTeamFilter = (filter: string) => ({
  type: ActionType.UpdateTeamFilter,
  payload: filter,
});

export function selectSearchParams(state: IAppState) {
  const {
    searchParams: {
      dateSelectionType,
      dateRange,
    },
  } = state.exchange;

  const searchParams = {
    ...defaultExchangeSearchParams,
  };
  const filters = [
    ...defaultExchangeFilters,
  ];

  switch (dateSelectionType) {
    case DATE_SELECTION_TYPE.DATE_RANGE:
      searchParams.fromDate = dateRange.from !== null ? moment(dateRange.from).utc().startOf('day').toDate() : null;
      searchParams.toDate = dateRange.to !== null ? moment(dateRange.to).utc().endOf('day').toDate() : null;
      break;
    default:
  }

  return {
    ...searchParams,
    filters,
  };
}

export const selectTeamFilter = (state: IAppState): string => (state.exchange.searchParams.teamFilter);

export interface ISelectedTeam {
  conferenceIndex: number;
  teamIndex: number;
  teamCode: string;
  teamName: string;
  teamId: string;
  isFocused: boolean;
  conferenceName: string;
}

const conferencesSelector = (state: IAppState) => state.exchange.searchParams.conferences;

export const selectSelectedTeams = createSelector(
  conferencesSelector,
  (conferences) => (conferences || []).reduce((selectedTeams, conference, conferenceIndex) => {
    conference.teams.forEach(({
      isSelected,
      teamName,
      teamCode,
      teamId,
      isFocused,
      conferenceName,
    }, teamIndex) => {
      if (isSelected) {
        selectedTeams.push({
          conferenceIndex,
          conferenceName,
          teamIndex,
          teamName,
          teamCode,
          teamId,
          isFocused,
        });
      }
    });
    return selectedTeams;
  }, []),
);

export const selectAllTeams = createSelector(
  conferencesSelector,
  (conferences) => (conferences || []).reduce((teams, conference, conferenceIndex) => {
    conference.teams.forEach(({
      teamName,
      teamCode,
      teamId,
      isFocused,
    }, teamIndex) => {
      teams.push({
        conferenceIndex,
        teamIndex,
        teamName,
        teamCode,
        teamId,
        isFocused,
      });
    });
    return teams;
  }, []),
);

export function addFilterToSearchParams(searchParams: any, filter: any) {
  return {
    ...searchParams,
    filters: [
      ...searchParams.filters,
      filter,
    ],
  };
}

export const toggleConferenceExpandedState = (conferenceIndex: number) => ({
  type: ActionType.ToggleConferenceExpandedState,
  payload: {
    conferenceIndex,
  },
});

export const toggleConferenceAllExpandedState = (isExpanded: boolean) => ({
  type: ActionType.ToggleConferenceAllExpandedState,
  payload: {
    isExpanded,
  },
});

function persistSelectedTeams(state: IAppState) {
  const selectedTeams = selectSelectedTeams(state);
  localStorage.setItem(TEAMS_SELECTION_STORAGE_KEY, JSON.stringify(selectedTeams.map(({ teamCode }) => teamCode)));
}

export const toggleFilterByQualifier = (qualifier?: Array<string>) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ToggleFilterByQualifier,
    payload: {
      qualifier,
    },
  });

  const state = getState();
  dispatch({
    type: ExchangeSearchResultsAction.UpdateAssetsOnFilterByQualifier,
    payload: {
      isFilterByQualifier: state.exchange.searchParams.isFilterByQualifier,
      qualifier,
    },
  });
};

export const toggleFilterByDownload = (downloadFilter?: DownloadFilter) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ToggleFilterByDownload,
    payload: {
      downloadFilter,
    },
  });

  const state = getState();
  dispatch({
    type: ExchangeSearchResultsAction.UpdateAssetsOnFilterByDownload,
    payload: {
      isFilterByDownload: state.exchange.searchParams.isFilterByDownload,
      downloadFilter,
      isPushToCloud: state.exchange.contentPreferences.subscription.isPushToCloud,
    },
  });
};

export const toggleFilterBySeason = (seasonFilter?: string) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ToggleFilterBySeason,
    payload: {
      seasonFilter,
    },
  });

  const state = getState();
  const {
    exchange: {
      searchParams: {
        seasonMap,
      },
    },
  } = state;
  dispatch({
    type: ExchangeSearchResultsAction.UpdateAssetsOnFilterBySeason,
    payload: {
      isFilterBySeason: state.exchange.searchParams.isFilterBySeason,
      seasonFilter,
      seasonMap,
    },
  });
};

export const toggleFilterByFileType = (fileTypeFilter?: Array<string>) => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ToggleFilterByFileType,
    payload: {
      fileTypeFilter,
    },
  });

  const state = getState();
  dispatch({
    type: ExchangeSearchResultsAction.UpdateAssetsOnFilterByFileType,
    payload: {
      isFilterByFileType: state.exchange.searchParams.isFilterByFileType,
      fileTypeFilter,
    },
  });
};

export const onTeamSelectionUpdate = (): IExchangeSearchParamsThunk => (
  dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  persistSelectedTeams(state);
};

export const toggleAllTeamsSelection = (conferenceIndex: number): IExchangeSearchParamsThunk => (
  dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ToggleAllTeamsSelection,
    payload: {
      conferenceIndex,
    },
  });
  dispatch(onTeamSelectionUpdate());
};

export const toggleTeamSelection = (conferenceName: string, teamId: string): IExchangeSearchParamsThunk => (
  dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ToggleTeamSelection,
    payload: {
      conferenceName,
      teamId,
    },
  });
  dispatch(onTeamSelectionUpdate());
};

export const clearTeamSelection = (): IExchangeSearchParamsThunk => (
  dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ClearTeamSelection,
  });
  dispatch(onTeamSelectionUpdate());
};

export const toggleTeamFocus = (conferenceIndex: number, teamIndex: number): IExchangeSearchParamsThunk => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ToggleTeamFocus,
    payload: {
      conferenceIndex,
      teamIndex,
    },
  });

  const state = getState();
  dispatch({
    type: ExchangeSearchResultsAction.UpdateAssetsOnFilterByFocus,
    payload: {
      selectedTeams: selectSelectedTeams(state),
    },
  });
};

export const queryTeams = (sport?: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  // TODO: optimize with hashmap if/when performance drops
  try {
    const storedTeams = localStorage.getItem(TEAMS_SELECTION_STORAGE_KEY);
    const previouslySelectedTeams = (storedTeams && storedTeams !== 'undefined') ? JSON.parse(storedTeams) : null;
    // eslint-disable-next-line
    const previouslySelected = (teamCode: string) => previouslySelectedTeams === null ? false : previouslySelectedTeams.includes(teamCode);
    const teamImageMap: { [key: string]: string } = {};

    const response = await getConferences(sport);
    const sortedConferences = _.sortBy(response.data.data, ['SortOrder']);
    let conferences: Array<IConference> = sortedConferences.map((conference) => ({
      _id: conference._id,
      conferenceName: conference.TeamCode,
      conferenceImageFile: conference.TeamImage,
      teams: [{
        teamId: conference._id,
        teamCode: conference.TeamCode,
        teamName: conference.TeamName,
        conferenceName: conference.TeamCode,
        division: conference.TeamDivision,
        teamImageFile: conference.TeamImage,
        isSelected: previouslySelected(conference.TeamCode),
        isFocused: false,
      }],
      isExpanded: false,
      isAllTeamsSelected: false,
    }));

    conferences.forEach(({ _id, conferenceImageFile }) => {
      teamImageMap[_id] = conferenceImageFile;
    });

    const teamRes = await getTeams(sport);
    const sortedTeams = _.sortBy(teamRes.data.data, ['TeamName']);
    sortedTeams.forEach((team) => {
      teamImageMap[team._id] = team.TeamImage;
      const conf = conferences.find((conference) => conference._id === team.TeamParentId);
      if (conf) {
        conf.teams.push({
          teamId: team._id,
          teamCode: team.TeamCode,
          teamName: team.TeamName,
          conferenceName: conf.conferenceName,
          division: team.TeamDivision,
          teamImageFile: team.TeamImage,
          isSelected: previouslySelected(team.TeamCode),
          isFocused: false,
        });
      }
    });

    conferences = conferences.reduce((newConferences, conference) => {
      newConferences.push({
        ...conference,
        isAllTeamsSelected: conference.teams.every(({ isSelected }) => isSelected),
      });
      return newConferences;
    }, []);

    dispatch({
      type: ActionType.LoadTeams,
      payload: {
        conferences,
        teamImageMap,
      },
    });
  } catch (e) {
    console.error('[QA] Error occurred while loading team data', e.toString());
  }
};

export const fetchScheduleSeasons = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const response = await getSchedules(false);
  const schedules = response.data?.data || [];

  const [_seasons, seasonMap] = schedules.reduce(([source, map], schedule) => {
    const { Season, _id } = schedule;
    source.add(Season);
    // eslint-disable-next-line
    map[_id] = Season;
    return [source, map];
  }, [new Set(), {} as SeasonMap]);

  let seasons = [..._seasons].sort() as Array<string>;

  const { exchange: { initExchange: { sport } } } = getState();

  // note: VA-2448 hockey formatting
  if (useCalendarSplit(sport)) seasons = seasons.map(padGameSeason);

  dispatch({
    type: ActionType.LoadSeasons,
    payload: {
      seasons,
      seasonMap,
    },
  });
};

export const clearDateSelection = () => ({
  type: ActionType.ClearDateSelection,
});

export const updateSportFilter = (sport: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  localStorage.removeItem(SEASON_FILTER_STORAGE_KEY);
  localStorage.removeItem(TEAMS_SELECTION_STORAGE_KEY);

  // Reset data and update sport
  dispatch(resetActiveAsset());
  dispatch(initialState());
  dispatch({
    type: ActionType.UpdateSportFilter,
    payload: {
      sportFilter: sport,
    },
  });

  // Get new lookups and teams according to sport
  await getLookups(sport)(dispatch, getState);
  await queryTeams(sport)(dispatch, getState);
};

export const loadVendorSports = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  // Check if user is vendor
  const isVendor = selectIsUserExchangeVendor(state);
  if (!isVendor) {
    return;
  }
  const vendorsData = await getVendors();
  const userOrgId = selectUserOrgIds(getState())[0];
  const vendor = vendorsData.data?.data?.find((v) => v.orgId === userOrgId);
  const sports = vendor?.availableQualifiers?.map((aq) => aq.sport) || [];

  dispatch({
    type: ActionType.UpdateVendorSports,
    payload: {
      vendorSports: sports,
    },
  });
};
